import { memo } from "react";
import { ClipboardList, Gavel, MicOff, UserCheck } from "lucide-react";
import type { Persona, Turn, TurnPlanPayload } from "../../domain/types";
import { useDebateStore } from "../../store/debateStore";
import { CitationAuditBadge } from "./CitationAuditBadge";
import { CritiqueBadge } from "./CritiqueBadge";
import { PersonaFeedbackBar } from "./PersonaFeedbackBar";
import { RagCitationsSubcard } from "./RagCitationsSubcard";
import { ResearchSubcard } from "./ResearchSubcard";
import { SaveToLibraryButton } from "./SaveToLibraryButton";
import { StreamingTokenizer } from "./StreamingTokenizer";
import { VerifyBadge } from "./VerifyBadge";
import { Pill } from "../common/Pill";
import { RoleIcon, roleLabel } from "../common/RoleIcon";
import { cn } from "../common/cn";
import { usePersonaTheme } from "../../hooks/usePersonaTheme";
import { useTurnFeedback } from "../../hooks/useSessionPersonaFeedback";
import { t } from "../../i18n";

interface TranscriptTurnProps {
  turn: Turn;
  persona: Persona | null;
  isCurrent: boolean;
  index?: number;
}

function severityBorder(turn: Turn): string {
  if (turn.outcome === "forfeited") return "border-l-amber-400/60";
  const sev = turn.critique?.severity;
  if (sev === "blocker") return "border-l-rose-500/70";
  if (sev === "major") return "border-l-amber-400/70";
  if (sev === "minor") return "border-l-slate-400/60";
  return "border-l-[hsl(var(--persona-glow)/0.6)]";
}

function fmtTime(ts: number): string {
  if (!ts) return "";
  const d = new Date(ts);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  const ss = d.getSeconds().toString().padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export const TranscriptTurn = memo(function TranscriptTurn({
  turn,
  persona,
  isCurrent,
  index = 0,
}: TranscriptTurnProps) {
  const themeStyle = usePersonaTheme(persona);
  const forfeited = turn.outcome === "forfeited";
  const name = persona?.displayName ?? "Bilinmeyen";
  const role = persona?.role ?? "moderator";

  // Kalite Planı overlay'leri — backend ilgili event'i göndermediyse
  // üçü de null/false kalır ve aşağıdaki koşullu render'lar hiç
  // tetiklenmez (eski davranış birebir korunur).
  const verify = useDebateStore((s) => s.verifyByTurnId[turn.id] ?? null);
  const plan = useDebateStore((s) => s.planByTurnId[turn.id] ?? null);
  const isWitness = useDebateStore((s) =>
    Boolean(s.witnessById[turn.speakerId]),
  );
  const isModerator = persona?.role === "moderator";

  // Persona Kütüphanesi — oylama YALNIZCA kalıcı bir kimliğin turunda
  // anlamlı. Geçici (wizard üretimi) bir personayı beğenmenin dersi
  // yazacağı bir yer yok; kontrolü göstermek boş bir vaat olurdu.
  const sessionId = useDebateStore((s) => s.sessionId);
  const { libraryPersonaId, verdict, setLocalVerdict } = useTurnFeedback(
    turn.id,
    turn.speakerId,
  );
  const spoke = !isModerator && !forfeited && turn.finalText !== null;
  const canRate = Boolean(libraryPersonaId) && Boolean(sessionId) && spoke;
  // Kütüphane-dışı (wizard üretimi) bir konuşmacı ise oy yerine "kaydet"
  // sunulur — bu kimlik tartışma bitince yok olacak, oy verilecek bir yer yok.
  const canPromote = !libraryPersonaId && spoke;

  return (
    <article
      style={{
        ...themeStyle,
        animationDelay: `${Math.min(index * 30, 300)}ms`,
      }}
      className={cn(
        "glass relative flex flex-col gap-3 rounded-xl border-l-[3px] px-4 py-3.5 sm:px-5 sm:py-4 turn-enter",
        severityBorder(turn),
        isCurrent && "bg-[hsl(var(--persona-glow)/0.04)] ring-1 ring-[hsl(var(--persona-glow)/0.5)] shadow-[0_0_0_1px_hsl(var(--persona-glow)/0.5),0_12px_32px_-12px_hsl(var(--persona-glow)/0.5)]",
        forfeited && "opacity-90",
      )}
      aria-current={isCurrent ? "true" : undefined}
      aria-live={isCurrent ? "polite" : undefined}
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "grid h-9 w-9 flex-none place-items-center rounded-full border text-white",
              "bg-[radial-gradient(circle_at_30%_25%,hsl(var(--persona-glow)/0.6),hsl(var(--persona-glow)/0.18))]",
              "border-[hsl(var(--persona-glow)/0.6)]",
            )}
            aria-hidden
          >
            {persona ? <RoleIcon role={role} className="h-4 w-4" /> : null}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-fg-strong">{name}</div>
            <div
              className="truncate text-[11px] uppercase tracking-wider text-fg-subtle"
              title={persona?.expertise || (persona ? roleLabel(role) : undefined)}
            >
              {persona ? persona.expertise?.trim() || roleLabel(role) : "—"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isModerator ? (
            <Pill
              tone="accent"
              className="text-[10px] uppercase tracking-wider"
              icon={<Gavel className="h-3 w-3" aria-hidden />}
              title="Oturumu yöneten moderatör — konsensüs oylamasını yönetir."
            >
              Oturum Başkanı
            </Pill>
          ) : null}
          {isWitness ? (
            <Pill
              tone="info"
              className="text-[10px] uppercase tracking-wider"
              icon={<UserCheck className="h-3 w-3" aria-hidden />}
              title="Moderatörün bir gereksinimi kapatmak için çağırdığı bilirkişi — görüşü kanıt sağlar, konsensüs oylamasına katılmaz."
            >
              Bilirkişi · oylamaya katılmaz
            </Pill>
          ) : null}
          <Pill monospace>TUR {turn.roundIndex || "-"}</Pill>
          <span className="hidden sm:inline font-mono text-[11px] tabular-nums text-fg-subtle">
            {fmtTime(turn.startedAt)}
          </span>
          {turn.critique ? <CritiqueBadge critique={turn.critique} /> : null}
          {turn.citationAudit ? <CitationAuditBadge audit={turn.citationAudit} /> : null}
          {verify ? (
            <VerifyBadge verify={verify.verdict} healJumps={verify.healJumps} />
          ) : null}
        </div>
      </header>

      {/* Kalite Planı — canlı plan ipucu. `turn.plan` geldiyse ve tur
          hâlâ akıyorsa (finalText yok) turn.thinking ipucu ailesiyle
          aynı yerde kısa bir "[planlıyor: …]" satırı gösterilir; tur
          bitince yerini aşağıdaki katlanabilir PlanSubcard alır. */}
      {plan && !forfeited && turn.finalText === null ? (
        <p className="flex items-center gap-1.5 text-[11px] italic leading-snug text-fg-subtle">
          <ClipboardList
            className="h-3 w-3 flex-none animate-pulse text-accent-300/80"
            aria-hidden
          />
          <span className="line-clamp-1" title={plan.angle}>
            [planlıyor: {plan.angle.trim() || "yaklaşım belirleniyor"}]
          </span>
        </p>
      ) : null}

      <div className="text-fluid-base leading-relaxed text-fg-strong">
        {forfeited ? (
          <p className="flex items-start gap-2 italic text-fg-muted">
            <MicOff className="mt-0.5 h-4 w-4 flex-none text-amber-300" aria-hidden />
            <span>
              {t("transcript.forfeitedNote")}
              {turn.forfeitReason ? (
                <span className="ml-1 text-fg-subtle">
                  {t("transcript.forfeitedReason", { reason: turn.forfeitReason })}
                </span>
              ) : null}
            </span>
          </p>
        ) : (
          <StreamingTokenizer
            turnId={turn.id}
            tokens={turn.tokens}
            finalText={turn.finalText}
            isActive={isCurrent && turn.finalText === null}
          />
        )}
      </div>

      {turn.research && turn.research.length > 0 ? (
        <div className="flex flex-col gap-2">
          {turn.research.map((r, idx) => (
            <ResearchSubcard
              key={`${turn.id}-research-${idx}`}
              research={r}
              defaultOpen={isCurrent}
            />
          ))}
        </div>
      ) : null}

      {turn.ragBatches && turn.ragBatches.length > 0 ? (
        <RagCitationsSubcard
          batches={turn.ragBatches}
          defaultOpen={isCurrent}
        />
      ) : null}

      {turn.thinking ? (
        <ThinkingSubcard
          chars={turn.thinking.chars}
          summary={turn.thinking.summary}
        />
      ) : null}

      {plan && turn.finalText !== null ? <PlanSubcard plan={plan} /> : null}

      {canRate && sessionId ? (
        <PersonaFeedbackBar
          turnId={turn.id}
          sessionId={sessionId}
          speakerId={turn.speakerId}
          personaName={name}
          {...(verdict ? { initialVerdict: verdict } : {})}
          onRecorded={(next) => setLocalVerdict(turn.id, next)}
        />
      ) : canPromote ? (
        <div className="flex justify-end border-t border-line-soft/40 pt-1.5">
          <SaveToLibraryButton speakerId={turn.speakerId} personaName={name} />
        </div>
      ) : null}
    </article>
  );
});

/**
 * ThinkingSubcard — collapsible "[uzman düşündü]" expander.
 *
 * The reasoning trace is non-spoken (TTS skips it) and never injected
 * into the spoken transcript / verdict synthesis. We expose it purely
 * for transparency: the operator can audit what the model considered
 * before answering. Closed by default to keep the bubble compact;
 * one click reveals the bounded head/tail summary the engine emitted.
 */
function ThinkingSubcard({
  chars,
  summary,
}: {
  chars: number;
  summary: string;
}) {
  return (
    <details className="group rounded-lg border border-violet-400/15 bg-violet-500/[0.04] px-3 py-2 text-xs text-fg-muted">
      <summary className="cursor-pointer list-none select-none">
        <span className="inline-flex items-center gap-1.5 font-medium text-violet-300/90 transition group-open:text-violet-200">
          <span aria-hidden>🧠</span>
          Uzmanın iç akıl yürütmesi
          <span className="ml-1 rounded-full border border-violet-400/20 bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-mono tabular-nums">
            {chars.toLocaleString("tr-TR")} char
          </span>
          <span className="ml-1 text-[10px] text-fg-subtle group-open:hidden">
            (göster)
          </span>
        </span>
      </summary>
      <pre className="mt-2 max-h-[18rem] overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-surface-0/40 p-2 font-mono text-[11px] leading-relaxed text-fg-muted">
        {summary}
      </pre>
    </details>
  );
}

/**
 * PlanSubcard — `turn.plan` payload'ının kalıcı görünümü.
 *
 * Canlı ipucu ("[planlıyor: …]") tur biter bitmez kaybolur; bu
 * expander ThinkingSubcard ile aynı katlanabilir desende planın
 * tamamını (açı, hedef gereksinimler, kullanılacak kanıt, çürütme
 * hedefi) denetlenebilir kılar. Kapalı başlar — bubble kompakt kalır.
 */
function PlanSubcard({ plan }: { plan: TurnPlanPayload }) {
  const angle = plan.angle.trim();
  return (
    <details className="group rounded-lg border border-accent-300/15 bg-[hsl(var(--accent)/0.04)] px-3 py-2 text-xs text-fg-muted">
      <summary className="cursor-pointer list-none select-none">
        <span className="inline-flex items-center gap-1.5 font-medium text-accent-300/90 transition group-open:text-accent-200">
          <ClipboardList className="h-3.5 w-3.5" aria-hidden />
          Tur planı{angle ? `: ${angle}` : ""}
          <span className="ml-1 text-[10px] text-fg-subtle group-open:hidden">
            (göster)
          </span>
        </span>
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        {plan.targetRequirements.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
              Hedef gereksinimler
            </div>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] leading-snug">
              {plan.targetRequirements.map((req, i) => (
                <li key={`req-${i}`}>{req}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {plan.evidenceToUse.length > 0 ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
              Kullanılacak kanıt
            </div>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] leading-snug">
              {plan.evidenceToUse.map((ev, i) => (
                <li key={`ev-${i}`} className="break-words">
                  {ev}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {plan.rebut ? (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
              Çürütme hedefi
            </div>
            <p className="mt-1 text-[11px] leading-snug">{plan.rebut}</p>
          </div>
        ) : null}
        {plan.targetRequirements.length === 0 &&
        plan.evidenceToUse.length === 0 &&
        !plan.rebut ? (
          <p className="text-[11px] italic text-fg-subtle">
            Plan yalnızca yaklaşım açısından ibaret — ek hedef bildirilmedi.
          </p>
        ) : null}
      </div>
    </details>
  );
}
